import { isTicketDeleted } from '@/utils/ticketDeletedState';
/**
 * FOH WORKBOARD — the read model.
 *
 * "Which advisor owns this repair order, what needs attention next, and what
 * are we waiting on?" — answered as a PURPOSE-BUILT VIEW of facts other
 * authorities already own. Nothing here is stored and nothing is decided
 * twice:
 *
 *   production   getProductionState            (Dayboard / OptiBoard / Calendar)
 *   lifecycle    getOptiBoardLifecycleTabForProductionState
 *   custody      ticket_custody                (who has the ball now)
 *   advisor      ticket_advisor_assignments    (whose RO it is — not custody)
 *   commercial   ticket_business_state         (on the commercial owner)
 *   handoff      latestMeaningfulHandoff       (the kick, never the accept)
 *   waits        business state awaiting_approval; an open PARTS pause
 *
 * The lanes are DERIVED presentation (deriveFohWorkboardLane). There is no
 * FOH status column and no way to drag a ticket between lanes: a card moves
 * because a fact moved.
 *
 * ONE CARD PER LOGICAL REPAIR ORDER. A multi-day rollover chain A ← B ← C is
 * one repair order owned by its head A (the same walk as the database's
 * rollover_job_owner). The card carries A's identity, advisor, custody
 * history and commercial state, and the LIVE segment's technician, timer and
 * location. A manual split part heads its own chain, so it stays its own card.
 */
import { getProductionState, type ProductionState, type ProductionStateTicketShape } from '@/utils/ticketProductionState';
import { getOptiBoardLifecycleTabForProductionState, type OptiBoardLifecycleTab } from '@/utils/optiBoardLifecycleTab';
import { getShopDayLabel } from '@/utils/shopTime';
import { stateLabel } from '@/lib/tickets/businessStateMachine';
import { getCustodyDwellAgingLevel, type CustodyAgingLevel } from '@/lib/tickets/lifecycle/custodyAging';
import type { CustodyStatus } from '@/lib/tickets/lifecycle/types';

// ── Inputs ─────────────────────────────────────────────────────────────────
export interface WorkboardTicket extends ProductionStateTicketShape {
  id: string;
  shop_id?: string | null;
  simple_ticket_number?: number | null;
  /** NOV3 A6: the repair's number (computed field). */
  repair_number?: number | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  type?: string | null;
  start_time?: string | null;
  due_out?: string | null;
  opti_finish?: string | null;
  rollover_of_ticket_id?: string | null;
  parent_ticket_id?: string | null;
  split_reason?: string | null;
  sequence_index?: number | null;
  vehicle?: { year?: number | string | null; make?: string | null; model?: string | null; vin?: string | null; license_plate?: string | null } | null;
  technician?: { id: string; name: string | null } | null;
}

export interface WorkboardCustody {
  ticket_id: string;
  custody_owner: 'FOH' | 'BOH' | string;
  custody_status: CustodyStatus | string;
  holder_user_id: string | null;
  entered_at: string;
}
export interface WorkboardAssignment { ticket_id: string; advisor_user_id: string | null }
export interface WorkboardBusinessState { ticket_id: string; current_state: string; entered_at?: string | null }
export interface WorkboardPause { ticket_id: string; pause_intent?: string | null; pause_reason?: string | null; pause_detail?: string | null }

/** OCT28 #3: an open pause that waits on the CUSTOMER's decision (approval / estimate), not on the shop. */
export const APPROVAL_PAUSE_DETAILS: ReadonlySet<string> = new Set(['customer_decision', 'estimate_needed']);

// ── Logical repair order ───────────────────────────────────────────────────
/** A segment superseded by a re-plan (or cancelled) is not part of the repair order. */
export function isTombstoneSegment(t: { deleted_at?: unknown; status?: string | null }): boolean {
  return isTicketDeleted({ status: t.status, deleted_at: t.deleted_at == null ? null : String(t.deleted_at) }) || t.status === 'Cancelled';
}

/**
 * The logical owner of a ticket among `byId`: walk rollover_of_ticket_id up
 * (rollover_job_owner's walk). When the predecessor was not loaded, an
 * EOD_ROLLOVER segment's parent_ticket_id is its chain head (rollover
 * builders stamp the root there); a non-rollover parent_ticket_id is NOT an
 * owner and is ignored.
 */
export function logicalOwnerId(t: WorkboardTicket, byId: ReadonlyMap<string, WorkboardTicket>): string {
  let cur = t;
  const seen = new Set<string>([t.id]);
  for (let i = 0; i < 64 && cur.rollover_of_ticket_id; i++) {
    const pred = byId.get(cur.rollover_of_ticket_id);
    if (!pred) {
      return cur.split_reason === 'EOD_ROLLOVER' && cur.parent_ticket_id ? cur.parent_ticket_id : cur.rollover_of_ticket_id;
    }
    if (seen.has(pred.id) || (pred.shop_id && cur.shop_id && pred.shop_id !== cur.shop_id)) break;
    seen.add(pred.id);
    cur = pred;
  }
  return cur.id;
}

export interface LogicalRepairOrder {
  /** The logical owner's ticket id — the repair order's identity. */
  id: string;
  owner: WorkboardTicket;
  members: WorkboardTicket[];
  /** The execution segment that is live now (running, else the earliest unfinished day). */
  live: WorkboardTicket;
  productionState: ProductionState;
  lifecycleStage: OptiBoardLifecycleTab | null;
  /** Current custody of the repair order (the most recently entered row across its segments). */
  custody: WorkboardCustody | null;
  advisorUserId: string | null;
  businessState: string | null;
  businessEnteredAt: string | null;
  /** An open technician pause whose stated intent is PARTS. */
  waitingOnParts: boolean;
  /** OCT28 #3: an open technician pause waiting on the customer's approval / an estimate. */
  waitingOnApproval: boolean;
}

const TERMINAL: ReadonlySet<ProductionState> = new Set(['completed_awaiting_pickup', 'picked_up', 'deleted', 'archived']);

function liveSegmentOf(members: WorkboardTicket[], owner: WorkboardTicket): WorkboardTicket {
  const running = members.find((m) => m.timer_status === 'running');
  if (running) return running;
  const open = members
    .filter((m) => !TERMINAL.has(getProductionState(m)))
    .sort((a, b) => (a.sequence_index ?? 1) - (b.sequence_index ?? 1)
      || String(a.scheduled_date ?? '').localeCompare(String(b.scheduled_date ?? '')));
  return open[0] ?? owner;
}

export function buildLogicalRepairOrders(input: {
  tickets: readonly WorkboardTicket[];
  custody?: readonly WorkboardCustody[];
  assignments?: readonly WorkboardAssignment[];
  businessStates?: readonly WorkboardBusinessState[];
  pauses?: readonly WorkboardPause[];
}): LogicalRepairOrder[] {
  const byId = new Map(input.tickets.map((t) => [t.id, t]));
  const groups = new Map<string, WorkboardTicket[]>();
  for (const t of input.tickets) {
    const owner = logicalOwnerId(t, byId);
    // A superseded segment never shapes the card — but the owner itself, even
    // if deleted, still decides that the whole repair order is deleted.
    if (isTombstoneSegment(t) && t.id !== owner) continue;
    const list = groups.get(owner) ?? [];
    list.push(t);
    groups.set(owner, list);
  }
  const custodyBy = new Map((input.custody ?? []).map((c) => [c.ticket_id, c]));
  const advisorBy = new Map((input.assignments ?? []).map((a) => [a.ticket_id, a.advisor_user_id]));
  const businessBy = new Map((input.businessStates ?? []).map((b) => [b.ticket_id, b]));
  const partsPaused = new Set((input.pauses ?? [])
    .filter((p) => String(p.pause_intent ?? '').toUpperCase() === 'PARTS' || String(p.pause_reason ?? '').toLowerCase() === 'parts')
    .map((p) => p.ticket_id));
  const approvalPaused = new Set((input.pauses ?? [])
    .filter((p) => APPROVAL_PAUSE_DETAILS.has(String(p.pause_detail ?? '')))
    .map((p) => p.ticket_id));

  const out: LogicalRepairOrder[] = [];
  for (const [ownerId, members] of groups) {
    const owner = byId.get(ownerId) ?? [...members].sort((a, b) => (a.sequence_index ?? 1) - (b.sequence_index ?? 1))[0];
    const live = liveSegmentOf(members, owner);
    // Open while ANY day still has work: then the live day speaks for the
    // repair order (a finished first day is "segment Completed", not repair
    // Completed). Closed: the owner carries the departure and the close.
    const open = members.some((m) => !TERMINAL.has(getProductionState(m)));
    const productionState = isTombstoneSegment(owner) ? 'deleted' : getProductionState(open ? live : owner);
    let custody: WorkboardCustody | null = null;
    for (const m of members) {
      const c = custodyBy.get(m.id);
      if (c && (!custody || Date.parse(c.entered_at) >= Date.parse(custody.entered_at))) custody = c;
    }
    const business = businessBy.get(owner.id) ?? null;
    out.push({
      id: owner.id,
      owner,
      members,
      live,
      productionState,
      lifecycleStage: getOptiBoardLifecycleTabForProductionState(productionState),
      custody,
      advisorUserId: advisorBy.get(owner.id) ?? null,
      businessState: business?.current_state ?? null,
      businessEnteredAt: business?.entered_at ?? null,
      waitingOnParts: members.some((m) => partsPaused.has(m.id)),
      waitingOnApproval: members.some((m) => approvalPaused.has(m.id)),
    });
  }
  return out;
}

// ── Lanes ──────────────────────────────────────────────────────────────────
export type FohLane = 'needs_attention' | 'waiting' | 'with_shop' | 'closeout' | 'upcoming' | 'done_today';

export const FOH_LANES: readonly FohLane[] = Object.freeze([
  'needs_attention', 'waiting', 'with_shop', 'closeout', 'upcoming', 'done_today',
]);

export const FOH_LANE_LABELS: Record<FohLane, string> = {
  needs_attention: 'Needs Attention',
  waiting: 'Waiting',
  with_shop: 'With the Shop',
  closeout: 'Closeout',
  upcoming: 'Upcoming',
  done_today: 'Done Today',
};

/** OCT28 #3: production states in which the repair is NOT finished, whatever its commercial state says. */
const STILL_IN_PRODUCTION: ReadonlySet<string> = new Set([
  'active_in_bay', 'held_in_bay', 'held_in_parking', 'parking_lot_inactive', 'scheduled', 'scheduled_but_parked',
]);

/** Commercial states that mean the work is done and the RO is in closeout. */
export const CLOSEOUT_BUSINESS_STATES: ReadonlySet<string> = new Set([
  'work_complete', 'ready_to_invoice', 'invoiced', 'paid', 'closed',
]);

export interface FohLanePlacement {
  lane: FohLane;
  /** Short truthful sub-state ("Working", "Customer approval", …). Never persisted. */
  detail: string;
}

/**
 * The ONE lane for a repair order, or null when it is not FOH work at all
 * (deleted / cancelled / archived, picked up on an earlier day, an
 * unassigned estimate). Precedence, first match wins:
 *
 *   1. DONE TODAY       picked up (picked_up_at) on today's shop day
 *   2. NEEDS ATTENTION  a handoff waiting to be accepted (pending_advisor_accept)
 *                       — even on finished work: someone must take it first
 *   3. CLOSEOUT         production completed awaiting pickup, or a closeout
 *                       commercial state (work_complete … closed) on work
 *                       that is NOT still live / paused / held / scheduled
 *                       (OCT28 #3)
 *   4. WAITING          ONLY an authoritative wait: an open PARTS pause
 *                       ("Parts"); commercial state awaiting_approval, or an
 *                       open customer-approval / estimate pause on held work
 *                       ("Customer approval", OCT28 #3)
 *   5. NEEDS ATTENTION  the front office holds it (with_advisor), or an
 *                       advisor-owned estimate to follow up
 *   6. WITH THE SHOP    BOH has it: working, held, kicked back to the tech
 *                       (pending_tech_accept), parked, scheduled today/overdue
 *   7. UPCOMING         scheduled for a later shop day, not started
 */
export function deriveFohWorkboardLane(
  ro: Pick<LogicalRepairOrder, 'productionState' | 'custody' | 'businessState' | 'waitingOnParts' | 'advisorUserId' | 'live' | 'owner'>
    & Partial<Pick<LogicalRepairOrder, 'waitingOnApproval'>>,
  ctx: { today: string; shopTimezone: string },
): FohLanePlacement | null {
  const p = ro.productionState;
  const status = ro.custody?.custody_status ?? 'with_tech';
  const biz = ro.businessState ?? null;

  if (p === 'deleted' || p === 'archived') return null;
  if (p === 'picked_up') {
    const at = ro.owner.picked_up_at ?? ro.live.picked_up_at;
    const day = at ? getShopDayLabel(String(at), ctx.shopTimezone) : null;
    return day === ctx.today ? { lane: 'done_today', detail: 'Picked up' } : null;
  }
  if (status === 'pending_advisor_accept') return { lane: 'needs_attention', detail: 'Handoff to accept' };
  if (p === 'completed_awaiting_pickup') {
    return { lane: 'closeout', detail: biz && CLOSEOUT_BUSINESS_STATES.has(biz) ? stateLabel(biz) : 'Completed — awaiting pickup' };
  }
  // OCT28 #3: a closeout commercial state never outranks production that is still live, paused, held or scheduled —
  // a paused / waiting repair is not "ordinary closeout" (a stale or early work_complete must not hide it).
  const held = p === 'held_in_bay' || p === 'held_in_parking';
  if (biz && CLOSEOUT_BUSINESS_STATES.has(biz) && !STILL_IN_PRODUCTION.has(p)) return { lane: 'closeout', detail: stateLabel(biz) };
  if (ro.waitingOnParts && held) return { lane: 'waiting', detail: 'Parts' };
  if (biz === 'awaiting_approval' || (ro.waitingOnApproval && held)) return { lane: 'waiting', detail: 'Customer approval' };
  if (status === 'with_advisor') return { lane: 'needs_attention', detail: 'With advisor' };
  if (p === 'estimate_prospect') {
    return ro.advisorUserId ? { lane: 'needs_attention', detail: 'Estimate — follow up' } : null;
  }

  const kickedBack = status === 'pending_tech_accept';
  switch (p) {
    case 'active_in_bay':
      return { lane: 'with_shop', detail: kickedBack ? 'Waiting for technician' : 'Working' };
    case 'held_in_bay':
      return { lane: 'with_shop', detail: kickedBack ? 'Waiting for technician' : 'Paused' };
    case 'held_in_parking':
      return { lane: 'with_shop', detail: kickedBack ? 'Waiting for technician' : 'Held' };
    case 'parking_lot_inactive':
      return { lane: 'with_shop', detail: kickedBack ? 'Waiting for technician' : 'Parked — not scheduled' };
    case 'scheduled':
    case 'scheduled_but_parked': {
      const day = ro.live.scheduled_date ? String(ro.live.scheduled_date).slice(0, 10) : null;
      if (kickedBack) return { lane: 'with_shop', detail: 'Waiting for technician' };
      if (day && day > ctx.today) return { lane: 'upcoming', detail: 'Scheduled' };
      if (day && day < ctx.today) return { lane: 'with_shop', detail: 'Overdue' };
      return { lane: 'with_shop', detail: p === 'scheduled_but_parked' ? 'Parked — scheduled today' : 'Scheduled today' };
    }
    default:
      return null;
  }
}

// ── Sorting (deterministic; nothing persisted) ─────────────────────────────
export interface WorkboardCard {
  ro: LogicalRepairOrder;
  placement: FohLanePlacement;
  aging: CustodyAgingLevel;
}

const AGING_RANK: Record<CustodyAgingLevel, number> = { urgent: 0, warning: 1, normal: 2 };
const t = (v: unknown): number => {
  const ms = v == null ? NaN : Date.parse(String(v));
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
};
const byNumber = (a: WorkboardCard, b: WorkboardCard) =>
  (a.ro.owner.simple_ticket_number ?? 0) - (b.ro.owner.simple_ticket_number ?? 0) || a.ro.id.localeCompare(b.ro.id);

export function compareWorkboardCards(lane: FohLane, a: WorkboardCard, b: WorkboardCard): number {
  switch (lane) {
    case 'needs_attention':
      return AGING_RANK[a.aging] - AGING_RANK[b.aging]
        || t(a.ro.custody?.entered_at) - t(b.ro.custody?.entered_at)
        || t(a.ro.owner.due_out) - t(b.ro.owner.due_out) || byNumber(a, b);
    case 'waiting':
      return t(a.ro.businessEnteredAt ?? a.ro.custody?.entered_at) - t(b.ro.businessEnteredAt ?? b.ro.custody?.entered_at)
        || t(a.ro.owner.due_out) - t(b.ro.owner.due_out) || byNumber(a, b);
    case 'with_shop': {
      const working = (c: WorkboardCard) => (c.placement.detail === 'Working' ? 0 : 1);
      return working(a) - working(b) || t(a.ro.owner.due_out) - t(b.ro.owner.due_out)
        || t(a.ro.live.opti_finish) - t(b.ro.live.opti_finish) || byNumber(a, b);
    }
    case 'closeout':
      return t(a.ro.owner.completed_at ?? a.ro.live.completed_at) - t(b.ro.owner.completed_at ?? b.ro.live.completed_at) || byNumber(a, b);
    case 'upcoming':
      return String(a.ro.live.scheduled_date ?? '').localeCompare(String(b.ro.live.scheduled_date ?? ''))
        || t(a.ro.live.scheduled_start_time ?? a.ro.live.start_time) - t(b.ro.live.scheduled_start_time ?? b.ro.live.start_time)
        || byNumber(a, b);
    case 'done_today':
      return t(b.ro.owner.picked_up_at) - t(a.ro.owner.picked_up_at) || byNumber(a, b);
    default:
      return byNumber(a, b);
  }
}

/** Repair orders → one card each, in its one lane, sorted. */
export function buildWorkboardLanes(
  ros: readonly LogicalRepairOrder[],
  ctx: { today: string; shopTimezone: string; nowIso: string },
): Record<FohLane, WorkboardCard[]> {
  const lanes = Object.fromEntries(FOH_LANES.map((l) => [l, [] as WorkboardCard[]])) as Record<FohLane, WorkboardCard[]>;
  for (const ro of ros) {
    const placement = deriveFohWorkboardLane(ro, ctx);
    if (!placement) continue;
    const aging = ro.custody ? getCustodyDwellAgingLevel(String(ro.custody.custody_status), ro.custody.entered_at, ctx.nowIso) : 'normal';
    lanes[placement.lane].push({ ro, placement, aging });
  }
  for (const l of FOH_LANES) lanes[l].sort((a, b) => compareWorkboardCards(l, a, b));
  return lanes;
}

// ── Advisor filter + search ────────────────────────────────────────────────
/** 'all' | 'mine' | 'unassigned' | an advisor's user id. "Mine" is ADVISOR responsibility, never custody. */
export type AdvisorFilter = 'all' | 'mine' | 'unassigned' | string;

export function matchesAdvisorFilter(ro: Pick<LogicalRepairOrder, 'advisorUserId'>, filter: AdvisorFilter, me: string | null): boolean {
  if (filter === 'all') return true;
  if (filter === 'unassigned') return ro.advisorUserId == null;
  if (filter === 'mine') return me != null && ro.advisorUserId === me;
  return ro.advisorUserId === filter;
}

/** "#TKT-0142". */
export function formatRoNumber(t: { repair_number?: number | null; simple_ticket_number?: number | null; id: string }): string {
  // NOV3 A6: the repair's number (the chain head's for every rollover day).
  const n = t.repair_number ?? t.simple_ticket_number;
  return n != null ? `#TKT-${String(n).padStart(4, '0')}` : `#${t.id.slice(0, 8)}`;
}

export function vehicleLine(t: WorkboardTicket): string {
  const v = t.vehicle;
  return v ? [v.year, v.make, v.model].filter(Boolean).join(' ') : '';
}

/**
 * A stored VIN / plate that is an encryption envelope (`v<digits>:…`, the
 * server's is_field_ciphertext). It is not the value: searching it would match
 * random hex — a ticket-number query like "12" would hit nearly every
 * encrypted vehicle.
 */
const searchablePii = (v: string | null | undefined): string | null =>
  v && !/^v\d+(:|$)/.test(v) ? v : null;

/** Ticket number, customer, phone, vehicle, VIN, plate (case-insensitive, ≥ 2 chars). */
export function matchesWorkboardSearch(ro: Pick<LogicalRepairOrder, 'owner' | 'members'>, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return true;
  const o = ro.owner;
  const hay = [
    formatRoNumber(o), o.simple_ticket_number != null ? String(o.simple_ticket_number) : '',
    o.customer_name, o.customer_phone, vehicleLine(o), searchablePii(o.vehicle?.vin), searchablePii(o.vehicle?.license_plate),
  ].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(q.replace(/^#?tkt-?/, '')) || hay.includes(q);
}

// ── Nav badge: the SAME logical repair orders and the SAME lane law ──────
/** The ticket columns lane derivation and logical-owner resolution read. */
export const FOH_LANE_TICKET_COLUMNS =
  'id, shop_id, simple_ticket_number, status, is_estimate, deleted_at, archived_at, picked_up_at, completed_at, '
  + 'paused_at, pause_reason, timer_status, actually_started, bay_id, parking_lot, parking_spot_id, assigned_tech_id, '
  + 'scheduled_tech_id, scheduled_date, scheduled_start_time, start_time, due_out, opti_finish, '
  + 'rollover_of_ticket_id, parent_ticket_id, split_reason, sequence_index';

export interface FohAttentionSummary {
  /** Repair orders in the Needs Attention lane — the badge number. */
  needsAttention: number;
  /** …of which a handoff is waiting to be accepted. */
  pendingAdvisorAccept: number;
  /** …of which the front office holds the ball. */
  withAdvisor: number;
  /** Repair orders kicked back and waiting on a technician (tone only; not FOH attention). */
  pendingTechAccept: number;
}

/**
 * The Needs Attention summary over LOGICAL repair orders, by the exact lane
 * rule the Workboard renders with (deriveFohWorkboardLane). A rollover chain
 * whose custody rows are split across A and B is one repair order and counts
 * once; two separate repair orders count twice.
 */
export function summarizeFohAttention(
  ros: readonly LogicalRepairOrder[],
  ctx: { today: string; shopTimezone: string },
): FohAttentionSummary {
  const out: FohAttentionSummary = { needsAttention: 0, pendingAdvisorAccept: 0, withAdvisor: 0, pendingTechAccept: 0 };
  for (const ro of ros) {
    const placement = deriveFohWorkboardLane(ro, ctx);
    if (!placement) continue;
    const status = ro.custody?.custody_status;
    if (placement.lane === 'needs_attention') {
      out.needsAttention += 1;
      if (status === 'pending_advisor_accept') out.pendingAdvisorAccept += 1;
      else if (status === 'with_advisor') out.withAdvisor += 1;
    } else if (status === 'pending_tech_accept') {
      out.pendingTechAccept += 1;
    }
  }
  return out;
}
